import type { WeightSampleLog } from '../types/production'

import { toLocalCalendarDayKey } from './dateNormalization'
import { farmTimeZone } from './farmTime'

/**
 * Canonical daily weight rollup for one (lot, calendar date) pair.
 *
 * Built by averaging every `WeightSampleLog.weightGrams` whose `recordedAt`
 * resolves to the same calendar day and whose `lot` matches.
 *
 * v2.1 finding #60 resolution: when any `WeightSampleLog` events exist for a
 * given `(lot, date)`, this rollup IS the canonical daily weight signal.
 * Consumers should prefer `meanWeightGrams` over any single event's
 * `weightGrams` and over the `ProductionRecord.weightGrams` field for that
 * day. Rows without any matching event fall back to the stored value.
 *
 * v2.2 population-weighted mean (Phase 7 Block C): `sampleCount` on
 * `WeightSampleLog` is `Int?` and existing rows may carry `null`. Per bucket
 * (one `(lot, calendar date)` group), this rollup branches:
 * - When **every** contributing log has `sampleCount != null && sampleCount >= 1`,
 *   `meanWeightGrams` is the population-weighted mean
 *   `Σ(sampleCount_i × weightGrams_i) / Σ(sampleCount_i)`.
 * - When **any** contributing log has `sampleCount == null` or `sampleCount < 1`,
 *   the whole bucket falls back to the arithmetic mean of `weightGrams`,
 *   byte-identical to pre-Block-C output. No synthetic `sampleCount = 1` is
 *   invented for uncounted rows.
 * The branch is evaluated per bucket, not per row — buckets are independent.
 */
export type DailyWeightRollup = {
  /** Calendar date key in `YYYY-MM-DD` form (farm-local interpretation). */
  date: string
  /** Trimmed lot label, or empty string when the source log omitted it. */
  lot: string
  /**
   * Population-weighted mean of `weightGrams` when every contributing log is
   * counted, or the arithmetic mean (fallback) when any log's `sampleCount`
   * is `null`/`< 1`. Decimal string, same rounding/formatting as before.
   */
  meanWeightGrams: string
  /** IDs of every log that contributed, in input encounter order. */
  logIds: string[]
  /** Number of contributing logs. Equal to `logIds.length`. */
  eventCount: number
  /** Latest `recordedAt` value among contributing logs (raw ISO string). */
  latestRecordedAt: string
  /** Sum of `weightGrams` across every contributing log — plain arithmetic sum, not the weighted numerator. */
  totalWeightGrams: number
  /**
   * Sum of `sampleCount` across every contributing log when **all** of them
   * are counted (`sampleCount != null && sampleCount >= 1`); `null` when the
   * bucket took the arithmetic fallback because at least one log was
   * uncounted.
   */
  totalSampleCount: number | null
}

const toCalendarDateKey = (raw: string | undefined | null, timeZone?: string): string => {
  // Group by the *local* calendar day, matching the mortality rollup contract
  // and the finding #7 + #47 timezone rules. A ProductionRecord stored as
  // "2026-04-13T19:36" (datetime-local, no Z) and its companion
  // WeightSampleLog recorded as "2026-04-14T00:36:00.000Z" (UTC) must land in
  // the same bucket for operators in behind-UTC timezones.
  return toLocalCalendarDayKey(raw, timeZone)
}

const toWeight = (raw: string | number | undefined | null): number => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0
  if (typeof raw !== 'string') return 0
  const trimmed = raw.trim()
  if (!trimmed) return 0
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : 0
}

const toLotKey = (raw: string | undefined | null): string => {
  if (typeof raw !== 'string') return ''
  return raw.trim()
}

/**
 * Parse a log's `sampleCount` into a usable weight for the population-weighted
 * mean. Returns `undefined` when the row is uncounted — `sampleCount == null`
 * or `< 1` — which per §2.2 sends its whole bucket to the arithmetic
 * fallback. Never defaults to `1`; that would silently mix a synthetic count
 * with genuinely counted rows.
 */
const toSampleCount = (raw: number | undefined | null): number | undefined => {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 1) return undefined
  return raw
}

/** Compose the `(date, lot)` lookup key used by the rollup map. */
export const buildDailyWeightRollupKey = (date: string, lot: string): string => {
  return `${date}::${lot}`
}

/**
 * Format the arithmetic mean of a set of contributing weights as a decimal
 * string with at most 3 fraction digits, matching how `ProductionRecord.weightGrams`
 * is typically displayed. Trailing zeros are trimmed for parity with the
 * pre-cluster sidecar behavior.
 */
const formatMeanWeight = (total: number, count: number): string => {
  if (count <= 0) return '0'
  const mean = total / count
  if (!Number.isFinite(mean)) return '0'
  const rounded = Math.round(mean * 1000) / 1000
  const asString = rounded.toString()
  return asString
}

/** Per-bucket internal accumulator for the population-weighted numerator. Not exposed on `DailyWeightRollup`. */
type WeightedAccumulator = {
  /** Σ(sampleCount_i × weightGrams_i) across counted logs only. */
  weightedNumerator: number
  /** Σ(sampleCount_i) across counted logs only. */
  sampleCountSum: number
  /** `true` while every log seen so far for this bucket is counted; one uncounted log latches it to `false` for good. */
  allCounted: boolean
}

/**
 * Group `WeightSampleLog` events by `(calendar date, lot)` and compute the
 * per-bucket mean of `weightGrams` — population-weighted when every
 * contributing log is counted, arithmetic-fallback otherwise (see the
 * `DailyWeightRollup` JSDoc and §2.2 of the Block C prompt).
 *
 * The returned map is keyed by `buildDailyWeightRollupKey(date, lot)`. Logs
 * whose `recordedAt` cannot be parsed into a calendar date are skipped.
 *
 * Accumulation strategy: incremental dual-accumulator (numerator +
 * denominator for the weighted branch, plus an `allCounted` flag) recomputed
 * on every append, matching the pre-existing incremental style rather than a
 * two-pass accumulate-then-finalize restructure.
 *
 * The function is pure and side-effect free. It does not mutate the input.
 */
export const rollupWeightSamplesByLotDay = (
  logs: readonly WeightSampleLog[]
): Map<string, DailyWeightRollup> => {
  const result = new Map<string, DailyWeightRollup>()
  const accumulators = new Map<string, WeightedAccumulator>()

  for (const log of logs) {
    // Findings #85: a record's day is its farm's day.
    const date = toCalendarDateKey(log.recordedAt, farmTimeZone(log.farmId))
    if (!date) continue

    const lot = toLotKey(log.lot)
    const key = buildDailyWeightRollupKey(date, lot)
    const weight = toWeight(log.weightGrams)
    const sampleCount = toSampleCount(log.sampleCount)
    const isCounted = sampleCount !== undefined

    const existing = result.get(key)
    const acc = accumulators.get(key)

    if (existing && acc) {
      existing.totalWeightGrams += weight
      existing.logIds.push(log.id)
      existing.eventCount = existing.logIds.length
      if (typeof log.recordedAt === 'string' && log.recordedAt > existing.latestRecordedAt) {
        existing.latestRecordedAt = log.recordedAt
      }

      acc.allCounted = acc.allCounted && isCounted
      if (isCounted) {
        acc.weightedNumerator += weight * sampleCount
        acc.sampleCountSum += sampleCount
      }

      if (acc.allCounted) {
        existing.meanWeightGrams = formatMeanWeight(acc.weightedNumerator, acc.sampleCountSum)
        existing.totalSampleCount = acc.sampleCountSum
      } else {
        existing.meanWeightGrams = formatMeanWeight(existing.totalWeightGrams, existing.eventCount)
        existing.totalSampleCount = null
      }
    } else {
      accumulators.set(key, {
        weightedNumerator: isCounted ? weight * sampleCount : 0,
        sampleCountSum: isCounted ? sampleCount : 0,
        allCounted: isCounted
      })
      result.set(key, {
        date,
        lot,
        totalWeightGrams: weight,
        meanWeightGrams: isCounted ? formatMeanWeight(weight * sampleCount, sampleCount) : formatMeanWeight(weight, 1),
        logIds: [log.id],
        eventCount: 1,
        latestRecordedAt: typeof log.recordedAt === 'string' ? log.recordedAt : '',
        totalSampleCount: isCounted ? sampleCount : null
      })
    }
  }

  return result
}

/**
 * Convenience lookup: get the rollup that matches a `(rawDate, lot)` pair,
 * normalizing the date the same way as `rollupWeightSamplesByLotDay`.
 */
export const findDailyWeightRollup = (
  rollup: ReadonlyMap<string, DailyWeightRollup>,
  rawDate: string,
  lot: string | undefined | null
): DailyWeightRollup | undefined => {
  const date = toCalendarDateKey(rawDate)
  if (!date) return undefined
  return rollup.get(buildDailyWeightRollupKey(date, toLotKey(lot)))
}
